import React, { useCallback, useState } from "react";
import { type ButtonIntent } from "../Button/Button";
import Dialog, { useDialog, DialogIntent } from "../Dialog/Dialog";
import type { PersistentComponentLayer } from "../../blocks/PersistentComponents/PersistentComponents";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmIntent?: ButtonIntent;
  loading?: boolean;
  disabled?: boolean;
  intent?: DialogIntent;
}

export type PushConfirmDialogOptions = Omit<ConfirmDialogProps, "open" | "onOpenChange" | "loading"> & {
  layer: PersistentComponentLayer;
  onCancel?: () => void;
};

type ConfirmDialogContentProps = Omit<ConfirmDialogProps, "open" | "onOpenChange"> & {
  onClose: () => void;
  onCancel?: () => void;
  closeOnConfirm?: boolean;
};

const ConfirmDialogContent = ({
  title,
  description,
  onConfirm,
  onClose,
  onCancel,
  closeOnConfirm = false,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",

  confirmIntent = "destructive",
  loading = false,
  disabled = false,
}: ConfirmDialogContentProps) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const busy = loading || isConfirming;

  const handleConfirm = () => {
    setIsConfirming(true);
    Promise.resolve(onConfirm()).finally(() => {
      setIsConfirming(false);
      if (closeOnConfirm) {
        onClose();
      }
    });
  };

  return (
    <Dialog.Content className="flex flex-col">
      <Dialog.Header>
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.Description>{description}</Dialog.Description>
        <Dialog.Controls />
      </Dialog.Header>
      <Dialog.Actions>
        <Dialog.Actions.CancelButton
          onClick={() => {
            onCancel?.();
          }}
          disabled={disabled || busy}
          label={cancelLabel}
        >
          {cancelLabel}
        </Dialog.Actions.CancelButton>
        <Dialog.Actions.PrimaryButton
          intent={confirmIntent}
          onClick={handleConfirm}
          loading={busy}
          disabled={disabled || busy}
          label={confirmLabel}
        >
          {confirmLabel}
        </Dialog.Actions.PrimaryButton>
      </Dialog.Actions>
    </Dialog.Content>
  );
};

const ConfirmDialog = ({ open, onOpenChange, intent = "compact", ...props }: ConfirmDialogProps) => {
  const handleClose = () => onOpenChange?.(false);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      intent={intent}
    >
      <ConfirmDialogContent
        {...props}
        onClose={handleClose}
      />
    </Dialog>
  );
};

export const useConfirmDialog = () => {
  const { pushDialog } = useDialog();

  const pushConfirmDialog = useCallback(
    ({ layer, onCancel, ...props }: PushConfirmDialogOptions) => {
      return pushDialog({
        layer,
        options: { intent: "compact" },
        component: ({ close }) => (
          <ConfirmDialogContent
            {...props}
            onCancel={onCancel}
            onClose={close}
            closeOnConfirm
          />
        ),
      });
    },
    [pushDialog],
  );

  return { pushConfirmDialog };
};

export default ConfirmDialog;
